import type { GraphQLFormattedError } from 'graphql';

import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { NotFoundError, RedirectError } from '#src/shared/error';

import { PAGE_SIZE } from '#src/app/pages/petitionGuides/category/details/shared/pagination';

import type { PetitionGuidesCategoryPageDataQuery, PetitionGuidesCategoryPageDataQueryVariables } from './api.graphql';
import { PetitionGuidesCategoryPageData as PetitionGuidesCategoryPageDataQueryDocument } from './api.graphql';

type CtaSection = NonNullable<PetitionGuidesCategoryPageDataQuery['landingPage']>['ctaSection'];

export type PetitionGuidesCategoryPageData = NonNullable<PetitionGuidesCategoryPageDataQuery['category']> & {
	ctaSection: CtaSection | null;
	categories: PetitionGuidesCategoryPageDataQuery['categories'];
	page: number;
	totalPages: number;
};

/**
 * A failure on the `category` path, or a pathless (request-level) error alongside a missing
 * `category`, must not fall through to the caller's null-check, where it would be misreported
 * as a 404 — a Contentful/resolver outage must surface as a 500 instead.
 */
function reportCategoryFetchErrorIfAny({
	hasCategory,
	errors,
	requestId,
	slug,
	report,
}: {
	hasCategory: boolean;
	errors: readonly GraphQLFormattedError[] | undefined;
	requestId: string | undefined;
	slug: string;
	report: UtilityContext['errorReporter']['report'];
}): void {
	const categoryErrored = errors?.some((error) => error.path?.[0] === 'category');
	const missingCategoryWithPathlessError = !hasCategory && errors?.some((error) => !error.path);

	if (categoryErrored || missingCategoryWithPathlessError) {
		// `cause` carries the underlying GraphQL errors: the message here is synthetic, and the default
		// error boundary only ever sees this Error.
		// The explicit report is deliberate on top of the one `errorHandlerLink` already made. That link
		// reports every GraphQL error at `warning` severity for trend detection; this is the separate
		// "the page could not render at all" signal, which belongs at `error`.
		const error = new Error('Failed to fetch petition guides category data', { cause: errors });
		void report({ error, params: { slug, requestId, errors } }, { severity: 'error' });
		throw error;
	}
}

export async function getPetitionGuidesCategoryPageData(
	{ gql: { fetch }, errorReporter: { report } }: UtilityContext,
	slug: string,
	page: number,
): Promise<PetitionGuidesCategoryPageData> {
	const { data, errors, requestId } = await fetch<
		PetitionGuidesCategoryPageDataQuery,
		PetitionGuidesCategoryPageDataQueryVariables
	>({
		query: PetitionGuidesCategoryPageDataQueryDocument,
		variables: { slug, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
		// rejectOnError: false — partial response: the CTA is optional, a resolver failure for it should not hide the category page.
		rejectOnError: false,
	});

	reportCategoryFetchErrorIfAny({ hasCategory: !!data?.category, errors, requestId, slug, report });

	// `category` is null (with no error on its path) when there's genuinely no matching
	// category slug — that's our real 404 signal. An empty `category.articles` list is a
	// legitimately empty category, not a 404.
	if (!data?.category) {
		throw new NotFoundError();
	}

	const totalPages = Math.max(1, Math.ceil(data.category.totalArticleCount / PAGE_SIZE));

	// Page 1 of a 0-article category has totalPages === 1, so it renders the empty state
	// instead of redirecting here.
	//
	// A page beyond the last one redirects to the bare category URL rather than 404ing: unlike an
	// unknown slug, this is a real category that simply doesn't have that many pages right now — it
	// might later, if content ops add articles — so treating it as permanently gone is wrong. The
	// redirect target uses the fetched `data.category.slug` rather than the input `slug`, matching
	// `pageCanonical`, and drops `page` entirely rather than forwarding it, since the whole point is
	// to land on a page number that's valid.
	if (page > totalPages) {
		throw new RedirectError({ url: `/petition-guides/category/${data.category.slug}` });
	}

	return {
		...data.category,
		ctaSection: data.landingPage?.ctaSection ?? null,
		categories: data.categories,
		page,
		totalPages,
	};
}
